import { useState } from 'react';
import { InlineNotification, TextArea } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import ErrorToast from 'components/error-toast';
// Turns on the ibm-products SidePanel.
import 'components/inline-edit';
import type { AreaVerdictKind } from 'types/area-workspace';
import type { AreaVerdictSetter } from './use-workspace-writes';
import styles from './area-verdict-panel.module.scss';

interface AreaVerdictPanelProps {
  /** The verdict being set, fixed by which bar button opened the panel. Null = closed. */
  kind: AreaVerdictKind | null;
  areaName: string;
  setter: AreaVerdictSetter;
  onClose: () => void;
}

/**
 * Mark as Correct / Mark as Incorrect for the workspace's area — the Well Status decision
 * panel, pointed at an area. The verdict is fixed by the button that opened it, and a
 * reason is required: marking a shape correct and promoting it stops a GIS re-ingest
 * from overwriting the area, which is worth saying why. A clear is posted from the bar
 * with the panel closed, so its refusal comes back as a toast instead.
 * @param {AreaVerdictPanelProps} props The verdict kind, the area's name, the setter and the close handler.
 * @return {JSX.Element} The panel.
 */
export function AreaVerdictPanel({
  kind,
  areaName,
  setter,
  onClose,
}: AreaVerdictPanelProps) {
  const [reason, setReason] = useState('');
  const [blurred, setBlurred] = useState(false);
  // Opening with the other verdict starts clean, so a reason written for "correct"
  // cannot ride along into an "incorrect".
  const [shownKind, setShownKind] = useState(kind);
  if (kind !== shownKind) {
    setShownKind(kind);
    setReason('');
    setBlurred(false);
  }

  const isCorrect = kind === 'correct';
  const trimmed = reason.trim();
  const missingReason = blurred && trimmed.length === 0;

  /** Saves the verdict with its reason, or flags the missing reason. */
  const handleSave = () => {
    if (!trimmed) {
      setBlurred(true);
      return;
    }
    if (!kind) return;

    setter.set(kind, trimmed, onClose);
  };

  return (
    <>
      <SidePanel
        open={kind !== null}
        onRequestClose={onClose}
        title={isCorrect ? 'Mark as Correct' : 'Mark as Incorrect'}
        subtitle={`Why are you marking ${areaName}'s shape as ${
          isCorrect ? 'correct' : 'incorrect'
        }?`}
        slideIn
        selectorPageContent="#main-content"
        placement="right"
        size="md"
        animateTitle={false}
        actions={[
          { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
          {
            kind: 'primary' as const,
            label: setter.saving ? 'Saving…' : 'Save',
            onClick: handleSave,
            disabled: setter.saving || trimmed.length === 0,
          },
        ]}
      >
        <div className={styles.body}>
          {isCorrect && (
            <p className={styles.note}>
              Once this workspace is promoted, a GIS import will no longer
              overwrite the area. Any later GIS change is held in a draft
              workspace for review.
            </p>
          )}
          {setter.error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Save failed"
              subtitle={setter.error}
            />
          )}
          <TextArea
            id="area-verdict-reason"
            labelText="Reason"
            placeholder={`Explain why this area's shape is ${
              isCorrect ? 'correct' : 'incorrect'
            }.`}
            value={reason}
            invalid={missingReason}
            invalidText={missingReason ? 'A reason is required.' : undefined}
            disabled={setter.saving}
            className={styles.field}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setBlurred(true)}
          />
        </div>
      </SidePanel>

      {!kind && setter.error && (
        <ErrorToast
          message="Could not update the verdict"
          subtitle={setter.error}
          onClose={() => {
            setter.reset();
            return true;
          }}
        />
      )}
    </>
  );
}
